import classNames from "classnames";
import { Button, LoadingDots } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import HoverCard from "components/DesignSystem/HoverCard/HoverCard";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { ArrowDown } from "components/icons/ArrowDown";
import { Cross } from "components/icons/Cross";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import {
  AddNewClass,
  AddNewGroup,
  ClassificationSelectorContent,
} from "components/Transaction/Slider/Classifications";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { useModal } from "hooks/useModal";
import { ComponentProps, Fragment, ReactNode, useState } from "react";
import { useGetAllClassificationQuery } from "store/apis/classifications";
import {
  Class,
  ClassificationGroup,
  SuggestedClassification,
} from "types/Models/classification";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import ConditionalToolTip from "components/design/conditionalToolTip";

type BaseClassificationAssignmentProps = {
  entityId: string;
  assignedClassificationIds: string[];
  onAssignClass: (
    classUuid: string,
    groupUuid: string,
    existingClassToRemove?: string
  ) => void;
  onRemoveClass: (classUuid: string) => void;
  suggestions?: SuggestedClassification[];
  onConfirmSuggestion?: (classUuid: string, groupUuid: string) => void;
  label?: string;
  required?: boolean;
  isDisabled?: boolean;
  readOnly?: boolean;
  variant?: "default" | "table";
  isUpdating?: boolean;
};

type ClassificationAssignmentWithTrigger = BaseClassificationAssignmentProps & {
  trigger: React.ReactNode;
  maxChips?: never;
};

type ClassificationAssignmentWithoutTrigger =
  BaseClassificationAssignmentProps & {
    trigger?: never;
    maxChips?: number;
  };

type ClassificationAssignmentProps =
  | ClassificationAssignmentWithTrigger
  | ClassificationAssignmentWithoutTrigger;

export const BaseClassificationAssignment = ({
  entityId,
  assignedClassificationIds,
  onAssignClass,
  onRemoveClass,
  suggestions = [],
  onConfirmSuggestion,
  label = "Assign Classification",
  required = false,
  isDisabled = false,
  readOnly = false,
  trigger,
  maxChips = 1,
  variant = "default",
  isUpdating = false,
}: ClassificationAssignmentProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const addNewGroupModal = useModal();
  const addNewClassModal = useModal();
  const [addClassToGroup, setAddClassToGroup] = useState<string | null>(null);

  const { data: allClassifications = [] } = useGetAllClassificationQuery(
    { entityId },
    { skip: !entityId }
  );

  const assignedClasses: ClassificationGroup[] = allClassifications
    .map((group) => ({
      ...group,
      classes: group.classes.filter((cls) =>
        assignedClassificationIds.includes(cls.uuid)
      ),
    }))
    .filter((group) => group.classes.length > 0);

  const hasSelectedValues = assignedClassificationIds.length > 0;
  const selectedCount = assignedClassificationIds.length;

  const handleClassificationSelect = (classUuid: string, groupUuid: string) => {
    const existingClassInGroup = allClassifications
      .find((group) => group.uuid === groupUuid)
      ?.classes.find((cls) => assignedClassificationIds.includes(cls.uuid));

    onAssignClass(classUuid, groupUuid, existingClassInGroup?.uuid);
  };

  const handleAddClass = (groupName: string) => {
    setAddClassToGroup(groupName);
    addNewClassModal.open();
  };

  const handleAddGroup = () => {
    addNewGroupModal.open();
  };

  const handleDropdownOpenChange = (open: boolean) => {
    if (readOnly || isPopoverOpen) {
      return;
    }

    if (open && isUpdating) return;

    setIsDropdownOpen(open);
  };

  return (
    <>
      <div className="t-flex t-flex-col t-gap-1 t-flex-1">
        {label && (
          <Label className="!t-pb-0" required={required}>
            {label}
          </Label>
        )}
        <Dropdown.Root
          open={isDropdownOpen}
          onOpenChange={handleDropdownOpenChange}
        >
          {trigger ? (
            <Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger>
          ) : (
            <Dropdown.Trigger asChild>
              <button
                type="button"
                disabled={isDisabled || readOnly}
                className={classNames(
                  "all:unset t-box-border t-flex t-w-full t-items-center t-justify-between t-rounded t-border t-border-solid  t-transition-all t-cursor-pointer t-group",
                  {
                    "t-opacity-50 t-cursor-not-allowed": isDisabled || readOnly,
                    "t-h-10 t-px-3 t-border-neutral-10 ": variant === "default",
                    "t-bg-surface-lighter-grey":
                      variant === "default" && !hasSelectedValues,
                    "t-h-8 t-px-2 hover:t-border-neutral-10 hover:t-bg-surface-grey  data-[state=open]:t-border-neutral-10 data-[state=open]:t-bg-surface-grey":
                      variant === "table",
                    "t-border-neutral-10 t-bg-surface-grey":
                      variant === "table" && isUpdating,
                    "t-border-neutral-0 t-bg-surface-lighter-grey":
                      variant === "table" && !hasSelectedValues && !isUpdating,

                    "t-border-surface-transparent":
                      variant === "table" && hasSelectedValues && !isUpdating,
                  }
                )}
              >
                <div className="t-flex t-items-center t-gap-2 t-flex-1 t-min-w-0">
                  {hasSelectedValues && assignedClasses.length > 0 ? (
                    <div className="t-flex t-items-center t-gap-2 t-flex-1 t-min-w-0">
                      {assignedClasses.slice(0, maxChips).map((group) =>
                        group.classes.map((classItem) => (
                          <Fragment key={classItem.uuid}>
                            <ConditionalToolTip
                              condition={
                                variant === "table" && (
                                  <>
                                    <span className="t-text-body-sm">
                                      {group.name}:
                                    </span>
                                    <span className="t-text-subtext-sm ">
                                      {" "}
                                      {classItem.name}
                                    </span>
                                  </>
                                )
                              }
                            >
                              <span>
                                <Tag
                                  tagType="dark_gray"
                                  icon={false}
                                  size="small"
                                >
                                  <div className="t-flex t-items-center t-gap-1 t-h-6">
                                    <button
                                      className={classNames(
                                        "all:unset t-truncate",
                                        {
                                          "t-max-w-28":
                                            variant === "table" &&
                                            selectedCount > 1,
                                          "t-max-w-32":
                                            variant === "table" &&
                                            selectedCount === 1,
                                        }
                                      )}
                                    >
                                      <span className="t-text-body-sm t-text-text-30">
                                        {group.name}:
                                      </span>
                                      <span className="t-text-subtext-sm t-text-text-60 t-ml-1">
                                        {classItem.name}
                                      </span>
                                    </button>
                                    {!readOnly && (
                                      <Button
                                        type="button"
                                        customType="ghost_icon"
                                        size="xsmall"
                                        className="t-flex-shrink-0 t-flex t-items-center t-justify-center !t-p-0 !t-h-4 t-border-0 t-bg-transparent hover:t-opacity-70"
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          onRemoveClass(classItem.uuid);
                                        }}
                                      >
                                        <Cross size="12" />
                                      </Button>
                                    )}
                                  </div>
                                </Tag>
                              </span>
                            </ConditionalToolTip>
                          </Fragment>
                        ))
                      )}
                      {selectedCount > maxChips && (
                        <HoverCard.Root
                          open={isPopoverOpen}
                          onOpenChange={setIsPopoverOpen}
                          openDelay={0}
                          closeDelay={100}
                        >
                          <HoverCard.Trigger asChild>
                            <button
                              type="button"
                              className="t-flex-shrink-0 t-rounded t-bg-neutral-10 t-p-1.5 t-text-caption t-text-text-30 t-border-none"
                            >
                              +{selectedCount - maxChips}
                            </button>
                          </HoverCard.Trigger>
                          <HoverCard.Portal>
                            <HoverCardPrimitive.Content
                              side="top"
                              align="center"
                              sideOffset={6}
                              className="t-p-3 t-z-dropdown t-rounded-md t-bg-surface t-text-body-sm t-shadow-lg"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div className="t-flex t-flex-col t-gap-2 t-max-w-xs t-overflow-auto">
                                {assignedClasses.slice(maxChips).map((group) =>
                                  group.classes.map((classItem) => (
                                    <Tag
                                      key={classItem.uuid}
                                      tagType="dark_gray"
                                      icon={false}
                                      size="small"
                                    >
                                      <div className="t-flex t-items-center t-gap-1 t-h-6">
                                        <span className="t-text-body-sm t-text-text-30">
                                          {group.name}:
                                        </span>
                                        <span className="t-text-subtext-sm t-text-text-60 t-ml-1">
                                          {classItem.name}
                                        </span>
                                        {!readOnly && (
                                          <Button
                                            type="button"
                                            customType="ghost_icon"
                                            size="xsmall"
                                            className="t-flex-shrink-0 t-flex t-items-center t-justify-center !t-p-0 !t-h-4 t-border-0 t-bg-transparent hover:t-opacity-70"
                                            onClick={(e) => {
                                              onRemoveClass(classItem.uuid);
                                              setIsPopoverOpen(false);
                                            }}
                                          >
                                            <Cross size="12" />
                                          </Button>
                                        )}
                                      </div>
                                    </Tag>
                                  ))
                                )}
                              </div>
                              <HoverCard.Arrow className="t-fill-white " />
                            </HoverCardPrimitive.Content>
                          </HoverCard.Portal>
                        </HoverCard.Root>
                      )}
                    </div>
                  ) : (
                    <div className="t-overflow-hidden t-text-ellipsis t-whitespace-nowrap t-text-text-30 t-text-body-sm">
                      Select
                    </div>
                  )}
                </div>
                {!readOnly && (
                  <div
                    className={classNames(
                      "t-transition-transform t-duration-200 t-text-text-100 t-flex t-items-center",
                      {
                        "-t-rotate-180": isDropdownOpen,
                        "t-opacity-0 t-w-0 group-hover:t-opacity-100 group-hover:t-w-auto group-data-[state=open]:t-opacity-100 group-data-[state=open]:t-w-auto":
                          variant === "table" && !isUpdating,
                        "t-opacity-100 t-w-auto":
                          variant === "table" && isUpdating,
                      }
                    )}
                  >
                    {isUpdating ? (
                      <LoadingDots />
                    ) : (
                      <ArrowDown strokeWidth="1.4" color="currentColor" />
                    )}
                  </div>
                )}
              </button>
            </Dropdown.Trigger>
          )}

          <Dropdown.Portal>
            <Dropdown.Content
              align="start"
              sideOffset={4}
              className="t-min-w-[var(--radix-dropdown-menu-trigger-width)] t-max-w-[var(--radix-dropdown-menu-trigger-width)]"
            >
              <ClassificationSelectorContent
                assignedClassificationIds={assignedClassificationIds}
                onAssignClass={handleClassificationSelect}
                classification={allClassifications}
                suggestions={suggestions}
                onConfirmSuggestion={onConfirmSuggestion}
                showAddGroup={true}
                onAddGroup={handleAddGroup}
                onAddClass={handleAddClass}
              />
            </Dropdown.Content>
          </Dropdown.Portal>
        </Dropdown.Root>
      </div>
      <AddNewGroup
        {...addNewGroupModal}
        entityId={entityId}
        onSuccess={(newGroup: ClassificationGroup) => {
          addNewGroupModal.close();
        }}
      />
      <AddNewClass
        {...addNewClassModal}
        entityId={entityId}
        addClassToGroup={addClassToGroup}
        onSuccess={(newClass: Class) => {
          addNewClassModal.close();
          setAddClassToGroup(null);
        }}
      />
    </>
  );
};

export const ClassificationAssignment = (
  props: ComponentProps<typeof BaseClassificationAssignment> & {
    blockedUI: ReactNode;
  }
) => {
  const subscriptionModal = useModal();

  return (
    <>
      <PermissionBasedUI
        feature={CLASSIFICATION_FEATURE_ACCESS}
        entityId={props.entityId}
        onUpgrade={subscriptionModal.open}
        blockedUI={props.blockedUI}
      >
        <BaseClassificationAssignment {...props} />
      </PermissionBasedUI>
      <BooksSoftwareSubscriptionsModal
        open={subscriptionModal.isOpen}
        onClose={() => subscriptionModal.close()}
        entityId={props.entityId}
        onProductSubscribe={() => subscriptionModal.close()}
        modalDescription={
          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
        }
      />
    </>
  );
};
